import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { reduzirImagem } from '@/lib/imagem';
import CartaoMinhaPresenca from './components/CartaoMinhaPresenca';
import Dica from '@/components/feature/Dica';
import CartaoOutraPessoa from './components/CartaoOutraPessoa';
import TelaConclusao from './components/TelaConclusao';
import { formatarDiaCurto, formatarHora } from './utils';
import type {
  FichaPresenca,
  FormacaoResposta,
  RegistrarResposta,
  ConviteDivulgar,
  SugestaoAposPresenca,
} from './types';

const DURACAO_AVISO_MS = 4000;
const MSG_REDE = 'Não foi possível confirmar agora. Tente novamente.';

export default function PresencaPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<FormacaoResposta | null>(null);

  const [confirmando, setConfirmando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const avisoTimer = useRef<number | null>(null);

  // Fluxo da foto de outra pessoa.
  const [fotoPara, setFotoPara] = useState<FichaPresenca | null>(null);
  const [enviando, setEnviando] = useState<number | null>(null);
  const [erroFoto, setErroFoto] = useState<{ inscricaoId: number; msg: string } | null>(null);
  const inputFotoRef = useRef<HTMLInputElement>(null);

  // Convites para mostrar quando a tela estiver terminada.
  const [convites, setConvites] = useState<ConviteDivulgar[] | null>(null);

  // Sugestão (ex.: Luau) mostrada só depois da presença confirmada.
  const [sugestao, setSugestao] = useState<SugestaoAposPresenca | null>(null);

  async function carregar(): Promise<FormacaoResposta | null> {
    const { data, error } = await supabase.rpc('formacao_pelo_token', {
      p_token: token ?? '',
    });
    if (error) throw error;
    return (data as FormacaoResposta) ?? null;
  }

  function mostrarAviso(msg: string) {
    setAviso(msg);
    if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    avisoTimer.current = window.setTimeout(() => setAviso(null), DURACAO_AVISO_MS);
  }

  useEffect(() => {
    let ativo = true;

    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/presenca/${token}`)}`, {
            replace: true,
          });
          return;
        }

        await garantirUsuario();

        const resp = await carregar();
        if (!ativo) return;

        if (!resp || resp.ok === false) {
          setErro(resp?.erro || 'Não foi possível carregar. Tente novamente.');
          return;
        }

        if (resp.precisa_login) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/presenca/${token}`)}`, {
            replace: true,
          });
          return;
        }

        setDados(resp);
      } catch {
        if (ativo) setErro('Não foi possível carregar. Tente novamente.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, pronto, user]);

  useEffect(
    () => () => {
      if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    },
    [],
  );

  // Busca os convites uma única vez, quando a tela fica terminada.
  useEffect(() => {
    if (!dados || dados.ok === false) return;
    const fichas = dados.fichas || [];
    const terminada = fichas.length > 0 && fichas.every((f) => f.presenca !== 'nenhuma');
    if (!terminada) return;
    if (convites !== null) return;
    if (!dados.evento_id) return;

    let ativo = true;
    (async () => {
      const { data, error } = await supabase.rpc('o_que_divulgar', {
        p_evento: dados.evento_id,
        p_onde: 'presenca',
      });
      if (!ativo) return;
      if (error) {
        setConvites([]);
      } else {
        setConvites(Array.isArray(data) ? (data as ConviteDivulgar[]) : []);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [dados, convites]);

  async function confirmarMinha() {
    if (confirmando) return;
    setConfirmando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('registrar_presenca', {
        p_token: token ?? '',
      });
      if (error) throw error;
      const resp = data as RegistrarResposta | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível confirmar. Tente de novo.');
        return;
      }
      mostrarAviso(resp.mensagem || 'Presença confirmada.');
      const novo = await carregar();
      if (novo && novo.ok !== false) setDados(novo);

      // Só depois da confirmação de verdade é que buscamos a sugestão.
      const fichasNovas = novo?.fichas || [];
      const minhaNova = fichasNovas.find((f) => f.e_minha) ?? null;
      const eventoId = novo?.evento_id ?? dados?.evento_id;
      if (minhaNova?.presenca === 'confirmada' && eventoId) {
        try {
          const { data: sData, error: sErr } = await supabase.rpc('sugestao_apos_presenca', {
            p_evento: eventoId,
          });
          if (!sErr) {
            const s = sData as SugestaoAposPresenca | null;
            setSugestao(s && s.mostrar ? s : null);
          }
        } catch {
          // Silêncio: se falhar, a confirmação continua valendo.
        }
      }
    } catch {
      setErroAcao(MSG_REDE);
    } finally {
      setConfirmando(false);
    }
  }

  function pedirFoto(ficha: FichaPresenca) {
    setErroFoto(null);
    setFotoPara(ficha);
  }

  async function abrirCamera() {
    if (!fotoPara) return;

    // Abre a câmera nativa direto, sem nenhuma espera antes do clique.
    // O clique precisa acontecer dentro do gesto do usuário — se houver um
    // `await` no meio, o navegador do celular ignora e a câmera não abre.
    inputFotoRef.current?.click();
  }

  async function aoEscolherFoto(e: ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0] ?? null;
    e.target.value = '';
    const ficha = fotoPara;
    setFotoPara(null);
    if (!ficha || !dados?.encontro_id) return;

    // Câmera recusada ou cancelada: o input volta vazio.
    if (!arquivo) {
      setErroFoto({
        inscricaoId: ficha.inscricao_id,
        msg: `Precisamos da câmera para confirmar a presença de ${ficha.nome}. Autorize o acesso à câmera nas configurações do navegador e tente de novo.`,
      });
      return;
    }

    setEnviando(ficha.inscricao_id);
    setErroFoto(null);
    try {
      if (!user) throw new Error('sessão');

      // Mesma regra de todo upload de foto: 800 px no lado maior, JPEG 0.7.
      const reduzido = await reduzirImagem(arquivo, 800, 0.7, 'image/jpeg');
      const caminho = `${user.id}/${dados.encontro_id}/${ficha.inscricao_id}-${Date.now()}.jpg`;

      const { error: errUp } = await supabase.storage
        .from('presencas')
        .upload(caminho, reduzido, { contentType: 'image/jpeg' });
      if (errUp) {
        setErroFoto({
          inscricaoId: ficha.inscricao_id,
          msg: 'Não consegui enviar a foto. Tente de novo.',
        });
        return;
      }

      const { data, error } = await supabase.rpc('registrar_presenca', {
        p_token: token ?? '',
        p_inscricao: ficha.inscricao_id,
        p_foto_url: caminho,
      });
      if (error) {
        setErroFoto({ inscricaoId: ficha.inscricao_id, msg: MSG_REDE });
        return;
      }
      const resp = data as RegistrarResposta | null;
      if (!resp || resp.ok === false) {
        setErroFoto({
          inscricaoId: ficha.inscricao_id,
          msg: resp?.erro || 'Não consegui enviar a foto. Tente de novo.',
        });
        return;
      }

      // Só aqui, com ok = true do servidor, é que muda o cartão.
      mostrarAviso(resp.mensagem || 'Foto enviada.');
      const novo = await carregar();
      if (novo && novo.ok !== false) setDados(novo);
    } catch {
      setErroFoto({ inscricaoId: ficha.inscricao_id, msg: MSG_REDE });
    } finally {
      setEnviando(null);
    }
  }

  if (carregando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4">
        <span className="flex h-10 w-10 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-3xl" />
        </span>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <p className="mt-4 text-base text-foreground-800">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="mt-6 flex h-14 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-base font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dados) return null;

  const fichas = dados.fichas || [];
  const minhaFicha = fichas.find((f) => f.e_minha) ?? null;
  const outras = fichas.filter((f) => !f.e_minha);
  const naJanela = dados.na_janela === true;
  const terminada = fichas.length > 0 && fichas.every((f) => f.presenca !== 'nenhuma');

  if (terminada) {
    return (
      <TelaConclusao
        fichas={fichas}
        meuNome={minhaFicha?.nome ?? ''}
        convites={convites ?? []}
        sugestao={sugestao}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF7EC]">
      {aviso && (
        <div className="fixed left-1/2 top-4 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-green-600">
              <i className="ri-check-double-line" />
            </span>
            {aviso}
          </div>
        </div>
      )}

      <input
        ref={inputFotoRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={aoEscolherFoto}
        className="hidden"
      />

      {fotoPara && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-6 sm:rounded-2xl">
            <p className="text-center text-3xl">💛</p>
            <p className="mt-2 text-center text-xl font-semibold text-foreground-950">
              Tire uma foto de vocês dois juntos, aqui na formação.
            </p>
            <button
              type="button"
              onClick={abrirCamera}
              className="mt-6 flex h-14 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-base font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Abrir câmera
            </button>
            <button
              type="button"
              onClick={() => setFotoPara(null)}
              className="mt-2 flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-800"
            >
              Voltar
            </button>
          </div>
        </div>
      )}

      <header className="bg-primary-600 px-4 py-6">
        <div className="mx-auto w-full max-w-[480px]">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/75">
            {dados.evento || ''}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="text-[17px] font-bold text-white">{dados.formacao || 'Formação'}</h1>
            <Dica id="presenca" claro />
          </div>
          <p className="mt-1 text-xs text-white/80">
            {formatarDiaCurto(dados.quando)}
            {dados.local ? ` · ${dados.local}` : ''}
          </p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5">
            <span
              className={`h-2 w-2 rounded-full ${
                dados.fechada ? 'bg-white/50' : 'bg-green-400 animate-pulse'
              }`}
            />
            <span className="text-[11px] font-medium text-white/90">
              {dados.fechada
                ? dados.motivo || 'Chamada fechada'
                : dados.fecha_em
                  ? `Chamada aberta até ${formatarHora(dados.fecha_em)}`
                  : 'Chamada aberta'}
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[480px] px-4 py-8">

        {!naJanela && (
          <div className="mt-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
            Esta formação não está acontecendo agora. Fale com a direção para marcar sua presença.
          </div>
        )}

        {erroAcao && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erroAcao}
          </div>
        )}

        {/* A minha presença */}
        {minhaFicha && (
          <div className="mt-6">
            <CartaoMinhaPresenca
              ficha={minhaFicha}
              naJanela={naJanela}
              confirmando={confirmando}
              aoConfirmar={confirmarMinha}
            />
          </div>
        )}

        {/* As outras pessoas */}
        {outras.length > 0 && (
          <div className="mt-8">
            <h2 className="text-base font-semibold text-foreground-950">
              Marcar presença de outra pessoa
            </h2>
            <div className="mt-3 space-y-3">
              {outras.map((ficha) => (
                <CartaoOutraPessoa
                  key={ficha.inscricao_id}
                  ficha={ficha}
                  naJanela={naJanela}
                  enviando={enviando === ficha.inscricao_id}
                  erro={
                    erroFoto?.inscricaoId === ficha.inscricao_id ? erroFoto.msg : null
                  }
                  aoTirarFoto={() => pedirFoto(ficha)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}